import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Button, Spin, message } from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import useQuery from "../../../../hooks/useQuery";
import AiVoiceRecording from "./AiVoiceRecording";
import AiAddForm from "./AiAddForm";
import {
  addVitalsMonitoringApi,
  processAudioGrbsApi,
  processAudioVitalsApi,
} from "../../../../services/nurse.services";
import dayjs from "dayjs";
import useCompactMonitoringLayout from "../../../../hooks/useCompactMonitoringLayout";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../IpEMR/Doctor/InitialInspection/ClosePopup";

const AiRecordingModal = ({
  open,
  type,
  date,
  refresh,
  setOpen,
  startRecord,
  setStartRecord,
}) => {
  const [recordedFile, setRecordedFile] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState("record"); // "record" or "vitals"
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const params = useQuery();
  const stopRecordingRef = useRef(null);
  const isCompact = useCompactMonitoringLayout();

  useEffect(() => {
    const aiData = aiResponse || {}; // new ai data
    form.setFieldsValue({
      ...aiData,
      action_time: aiData?.action_time ? dayjs.utc(aiData.action_time) : null,
    });
  }, [aiResponse]);

  // Reset modal data when opened/closed
  useEffect(() => {
    if (open) {
      setRecordedFile(null);
      setAiResponse(null);
      setFormStep("record");
      form.resetFields();

      // Start recording after a brief delay
      setTimeout(() => {
        setStartRecord(true);
      }, 100);
    } else {
      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
    }
  }, [open]);

  const onSubmit = async (values) => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setLoading(true);
    const combinedDateTime = null;

    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);
      fd.append("action_time", JSON.stringify(combinedDateTime));
      const response =
        type === "vitals"
          ? await processAudioVitalsApi(fd)
          : await processAudioGrbsApi(fd);
      setFormStep("vitals");
      setAiResponse(response?.data || {});

      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setLoading(false);
    }
  };

  // Submit vital form
  const onVitalSubmit = async (values) => {
    const selectedTime = values?.action_time
      ? dayjs(values?.action_time).format("HH:mm")
      : null;
    const combinedDateTime =
      selectedTime &&
      dayjs(date)
        .set("hour", dayjs(values?.action_time).hour())
        .set("minute", dayjs(values?.action_time).minute())
        .format("YYYY-MM-DDTHH:mm:ss");

    const grbsFlag = type === "grbs" && { is_grbs: true };
    const updatedValues = {
      ...values,
      action_time: combinedDateTime,
      ...grbsFlag,
    };

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    addVitalsMonitoringApi(params?.patient, updatedValues)
      ?.then((res) => {
        message.success(res?.data?.message);
        setOpen(false);
        refresh();
      })
      .catch((err) => {
        message?.error(err?.response?.data?.message);
      });
  };

  const handleCancel = () => {
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onCancel={isCompact ? handleCancel : undefined}
      closeIcon={isCompact ? undefined : null}
      title={
        formStep === "record"
          ? "Recorder"
          : type === "vitals"
          ? "Add Vitals"
          : "Add GRBS"
      }
      width={isCompact ? "calc(100vw - 32px)" : "min(720px, calc(100vw - 48px))"}
      centered
      wrapClassName={isCompact ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
      getContainer={isCompact ? () => document.body : undefined}
      className={
        formStep === "record"
          ? "nurse-monitoring-recorder-modal"
          : "nurse-monitoring-vitals-modal"
      }
      maskClosable={false}
      keyboard={false}
      destroyOnClose={isCompact || undefined}
      footer={
        <div className="flex-end" style={{ gap: isCompact ? 12 : 24 }}>
          <Form.Item>
            <Button
              type="primary"
              ghost
              className={btnStyles.mdBtn}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              type="primary"
              className={btnStyles.mdBtn}
              disabled={loading}
              onClick={() => {
                if (formStep === "record") {
                  onSubmit();
                } else if (formStep === "vitals" && formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip="Processing..." />
        </div>
      ) : (
        <Form
          layout="vertical"
          ref={formRef}
          form={form}
          onFinish={onVitalSubmit}
        >
          {/* 🎙 Step 1: Voice Recorder */}
          {formStep === "record" && (
            <AiVoiceRecording
              recordedFile={recordedFile}
              setRecordedFile={setRecordedFile}
              startRecord={startRecord}
              setStartRecord={setStartRecord}
              stopAllRecordings={stopRecordingRef}
              type={type}
              addForm={true}
            />
          )}

          {/* 💊 Step 2: AI vital Add Form */}
          {formStep === "vitals" && (
            <>
              <AiAddForm form={form} editData={aiResponse} type={type} />
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default AiRecordingModal;
